import React from "react";
import * as Sentry from "@sentry/react";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, isNil } from "lodash";
import { Navbar, NavDropdown } from "react-bootstrap";
import { useAuth0 } from "utils/react-auth0-wrapper";
import { intercomHashSelector } from "store/selectors/auth0";
import {
  billingVerificationPending,
  employerHasFullyKybd,
} from "store/selectors/employer";
import { FiCreditCard, FiFileText } from "react-icons/fi";

import queryString from "query-string";
import EmployerSwitcher from "components/EmployerSwitcher";
// import EmployerReferralLink from "components/EmployerReferralLink";
import "components/navbar/NavBar.scss";
import NavBrand from "components/navbar/NavBrand";
import IconNavDropdown from "components/navbar/IconNavDropdown";
import SideBarToggleButton from "components/navbar/SideBarToggleButton";
import NavLoginHeaderText from "components/navbar/NavLoginHeaderText";
import type { RootState } from "types/state";

// Props a parent passes directly. `showChangeEmail` is declared in the original
// propTypes but is not redux-injected and not `client`, so it lives here. It is
// not consumed in the body today, hence optional.
interface EmployerNavBarOwnProps {
  showChangeEmail?: boolean;
}

const mapStateToProps = (state: RootState) => {
  const billingPortalURL = billingVerificationPending(state)
    ? null
    : // employer.billingPortalURL is `unknown` in RootState; the runtime value
      // is a URL string or null — cast at this boundary.
      (get(state.employer, "billingPortalURL", null) as string | null);

  return {
    userState: get(state.user, "userState.state", ""),
    userId: state.user.id,
    intercomHash: intercomHashSelector(state),
    isMultiAccount: state.employer.isMultiAccount,
    billingPortalURL,
    // canReferCompany: canReferCompany(state),
    employerHasKYB: employerHasFullyKybd(state),
  };
};

const mapDispatchToProps = {
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type EmployerNavBarProps = PropsFromRedux & EmployerNavBarOwnProps;

const EmployerNavBar = ({
  push,
  intercomHash,
  userId,
  isMultiAccount,
  billingPortalURL,
  // canReferCompany,
  employerHasKYB,
}: WithApolloClient<EmployerNavBarProps>) => {
  const { user, isAuthenticated, loginWithRedirect, logout } = useAuth0();

  if (!isNil(userId)) {
    window.analytics.identify(
      userId,
      {
        email: user!.email,
        invitationCode: queryString.parse(window.location.search)
          .invitationCode,
      },
      // @ts-expect-error Segment's identify accepts a 3rd `options` arg
      // (integrations) at runtime; the global type in types/globals.d.ts only
      // declares 2. Preserving the original runtime call.
      {
        integrations: {
          Intercom: {
            user_hash: intercomHash,
          },
        },
      }
    );
    Sentry.setUser({ id: userId });
  }

  // Preserved quirk: the original handler is async with no await — kept as-is.
  // eslint-disable-next-line @typescript-eslint/require-await -- async with no await is pre-existing behavior, not changed in conversion
  const logoutWithRedirect = async () => {
    window.analytics.track("Logged Out");
    // Segment's reset() is a real runtime method, but the global
    // Window["analytics"] type in types/globals.d.ts does not declare it —
    // cast at this boundary until that global type gains reset().
    (
      window.analytics as typeof window.analytics & { reset: () => void }
    ).reset();

    logout({
      logoutParams: {
        returnTo: window.location.origin + "/login",
      },
    });
  };

  return (
    <div>
      <header id="header" className={"icon-header"}>
        <Navbar>
          <div className="nav-left">
            <NavBrand />
            <Navbar.Text>
              <p className="portal">{"Employer"}</p>
            </Navbar.Text>
          </div>

          <div className="employer-switcher">
            {isMultiAccount && <EmployerSwitcher />}
          </div>

          <div className="nav-right">
            {!isAuthenticated && (
              // NavLoginHeaderText calls onClick with a v1-style { redirect_uri },
              // which auth0-spa-js v2 ignores (redirect_uri moved under
              // authorizationParams) — the arg is inert. Pre-existing behavior,
              // preserved via this cast rather than reshaped.
              <NavLoginHeaderText
                onClick={
                  loginWithRedirect as unknown as (args: {
                    redirect_uri: string;
                  }) => void
                }
              />
            )}

            {isAuthenticated && (
              <>
                <IconNavDropdown
                  userName={user!.nickname}
                  // Preserved: logoutWithRedirect is async but onLogout expects
                  // () => void; fire-and-forget matches the original behavior.
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- pre-existing fire-and-forget async handler, not changed in conversion
                  onLogout={logoutWithRedirect}
                >
                  {billingPortalURL && (
                    <NavDropdown.Item
                      eventKey="4.3"
                      href={billingPortalURL}
                      onClick={() => {
                        window.analytics.track("Billing Portal Link Clicked");
                      }}
                      target={"_blank"}
                      rel="noopener noreferrer"
                    >
                      <FiCreditCard
                        color={"#FFFFFF"}
                        fill={"#FFFFFF"}
                        stroke={"#32325d"}
                        className="dropdown-icons"
                        size={14}
                      />
                      Billing
                    </NavDropdown.Item>
                  )}
                  {employerHasKYB && (
                    <>
                      <NavDropdown.Item
                        eventKey="4.2"
                        onClick={() => push("/dashboard/documents")}
                      >
                        <FiFileText
                          color={"#FFFFFF"}
                          fill={"#FFFFFF"}
                          stroke={"#32325d"}
                          className="dropdown-icons"
                          size={14}
                        />
                        Documents
                      </NavDropdown.Item>
                    </>
                  )}
                </IconNavDropdown>

                <SideBarToggleButton />
              </>
            )}
          </div>

          {/* <div className="employer-referral">
            {canReferCompany && <EmployerReferralLink />}
          </div> */}
        </Navbar>
      </header>
    </div>
  );
};

export default connector(withApollo<EmployerNavBarProps>(EmployerNavBar));
